import { useAemConfig } from '@as-react/common-components';
import { customerQueries } from '@as-react/common-queries';
import { LocationSearchType } from '@as-react/common-types';
import { useQuery } from '@tanstack/react-query';
import OsmAPI from '../../../../api/open-street-map/OsmApi';
import StoreAPI from '../../../../api/store/StoreAPI';
import { SEARCH_STORE_STORAGE_KEY, SHOP_NUMBER_STORAGE_KEY } from '../../../../constants/StorageKeysConstants';
import { LOCATION_TYPE } from '../../../../constants/StoreFinderConstants';
import { useOAuth } from '../../../../hooks/useOAuth';
import LocalStorageUtil from '../../../../util/LocalStorageUtil';
import { getLocationFromBrowser } from '../../../../util/LocationUtil';
import StoreUtil from '../../../../util/StoreUtil';
const initLocation = {
    lat: '',
    lng: '',
};
const initLocationSearchResult = {
    location: initLocation,
    locationName: '',
    type: LocationSearchType.DEFAULT,
    state: 'INIT',
};
export const useUserLocation = ({ shouldCheckLocation = true }) => {
    var _a;
    const { defaultRequestParams, servicesEndpoint, countryCodes, defaultCountryCode, isShopLanding } = useAemConfig();
    const { isLoggedIn } = useOAuth();
    const locationFromStorage = LocalStorageUtil.getJsonItem(SEARCH_STORE_STORAGE_KEY);
    const { getProfile } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const { data: profile } = useQuery(Object.assign(Object.assign({}, getProfile()), { enabled: Boolean(isLoggedIn) }));
    const enableQuery = {
        shoplanding: shouldCheckLocation && isShopLanding,
        localStorage: shouldCheckLocation && !isShopLanding && !!locationFromStorage,
        browserGeolocation: shouldCheckLocation && !isShopLanding && !locationFromStorage,
        profile: shouldCheckLocation && isLoggedIn && !isShopLanding && !locationFromStorage,
    };
    const browserLocationQueryResult = useQuery({
        queryKey: ['browserLocation'],
        queryFn: () => getLocationFromBrowser(),
        enabled: enableQuery.browserGeolocation,
    });
    const { data: browserLocationData, error: browserLocationError, status: browserLocationStatus, } = browserLocationQueryResult;
    const osmAddressQueryResult = useQuery({
        queryKey: ['addressLocation', browserLocationData],
        queryFn: () => OsmAPI.getAddressFromOSM(browserLocationData),
        enabled: browserLocationStatus !== 'pending' && !!browserLocationData && !browserLocationError,
        select: data => {
            if (data === null || data === void 0 ? void 0 : data.error) {
                return Object.assign(Object.assign({}, initLocationSearchResult), { state: 'ERROR' });
            }
            const locationName = `${data.address.road ? data.address.road : ''}${data.address.road && (data.address.town || data.address.city) ? ', ' : ''}${data.address.town ? data.address.town : data.address.city ? data.address.city : ''}`;
            return {
                location: {
                    lat: data.lat,
                    lng: data.lon,
                },
                locationName,
                type: LocationSearchType.SHARE_MY_LOCATION,
                state: 'SET',
            };
        },
    });
    const locationName = locationFromStorage ? locationFromStorage : ((_a = profile === null || profile === void 0 ? void 0 : profile.address) === null || _a === void 0 ? void 0 : _a.city) || '';
    const isPostCode = !!locationFromStorage && StoreUtil.isSearchByPostCode(locationName);
    const osmLocationQueryResult = useQuery({
        queryKey: ['locationLocation', locationName],
        queryFn: () => OsmAPI.getLocationFromOSM(isPostCode ? StoreUtil.formatPostCode(locationName, defaultCountryCode) : locationName, isPostCode ? defaultCountryCode : countryCodes),
        enabled: !!locationName && (enableQuery.localStorage || enableQuery.profile),
        select: data => {
            const osmLocationByImportance = data
                .filter(place => LOCATION_TYPE.includes(place.type))
                .sort((a, b) => b.importance - a.importance);
            if (osmLocationByImportance.length > 0) {
                return {
                    location: {
                        lat: osmLocationByImportance[0].lat,
                        lng: osmLocationByImportance[0].lon,
                    },
                    locationName: locationName,
                    type: LocationSearchType.DEFAULT,
                    state: 'SET',
                };
            }
            return;
        },
    });
    const storeNumber = LocalStorageUtil.getJsonItem(SHOP_NUMBER_STORAGE_KEY);
    const shoplandingQueryResult = useQuery({
        queryKey: ['shopLandingLocation', storeNumber],
        queryFn: () => StoreAPI.getShoplandingInfo(servicesEndpoint, defaultRequestParams.market, storeNumber),
        enabled: isShopLanding && !!storeNumber,
        select: data => {
            if (data === null || data === void 0 ? void 0 : data.geographic) {
                return {
                    location: {
                        lat: data.geographic.latitude,
                        lng: data.geographic.longitude,
                    },
                    locationName: '',
                    type: LocationSearchType.DEFAULT,
                    state: 'SET',
                };
            }
            return;
        },
    });
    if (isShopLanding) {
        return shoplandingQueryResult;
    }
    if ((enableQuery.localStorage || enableQuery.profile) && osmLocationQueryResult.data) {
        return osmLocationQueryResult;
    }
    if (browserLocationError) {
        return browserLocationQueryResult;
    }
    return osmAddressQueryResult;
};
